import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw, Search, Plus, List, LayoutGrid, Users, X } from "lucide-react";
import { StaffPinGate } from "../components/StaffPinGate";
import { hasCheckinAuth, PIN_KEY } from "../lib/checkinApi";
import { fetchSalaSettings, fetchSalaBookings, fetchSalaMonth, setSalaClosure, searchSalaBookings, patchSalaBooking, syncTheFork, syncCassa, ACTIVE, fetchGroups, mergeTables, moveTables, resetDayPositions, alignLayout, resetDefaultLayout, saveLayout, bookingTables } from "../lib/salaApi";
import { PendingTimeList } from "../components/sala/PendingTimeList";
import { CassaSummary } from "../components/sala/CassaSummary";
import { ClosureBanner } from "../components/sala/ClosureBanner";
import { DayPicker, DayPickerIcon } from "../components/sala/DayPicker";
import { BookingList } from "../components/sala/BookingList";
import { FloorPlan } from "../components/sala/FloorPlan";
import { BookingDrawer } from "../components/sala/BookingDrawer";
import { WalkInDialog } from "../components/sala/WalkInDialog";

const iso = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 10); };
const shift = (day, n) => { const d = new Date(day + "T12:00:00"); d.setDate(d.getDate() + n); return iso(d); };
const longDate = (day) => new Date(day + "T12:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });

export default function SalaPage() {
  const [authed, setAuthed] = useState(hasCheckinAuth());
  const [settings, setSettings] = useState(null);
  const [day, setDay] = useState(iso(new Date()));
  const [service, setService] = useState(new Date().getHours() < 17 ? "pranzo" : "cena");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [walkIn, setWalkIn] = useState(null); // false | { table }
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [mobileView, setMobileView] = useState("list");
  const [cassa, setCassa] = useState(null);
  const [cassaLoading, setCassaLoading] = useState(false);
  const [monthInfo, setMonthInfo] = useState(null);
  const [closureKey, setClosureKey] = useState(0);
  const [groups, setGroups] = useState([]);

  const loadMonth = useCallback(() => fetchSalaMonth(day.slice(0, 7)).then(setMonthInfo).catch(() => {}), [day]);
  useEffect(() => { if (authed) loadMonth(); }, [authed, loadMonth]);
  const applyClosure = async (date, services) => {
    try {
      const r = await setSalaClosure(date, services);
      toast.success(services.length ? `Chiusura salvata${r.affected_bookings ? ` · ${r.affected_bookings} prenotazioni già presenti: le gestisci tu` : ""}` : "Giornata riaperta");
      setClosureKey((k) => k + 1); loadMonth();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore chiusura"); }
  };

  const loadCassa = useCallback(async (force = false) => {
    setCassaLoading(true);
    try {
      const c = await syncCassa(day, force);
      setCassa(c);
      if (c?.sync?.events?.length) {
        c.sync.events.filter((e) => e.kind === "receipt" && e.booking).forEach((e) => toast.success(`${e.table} · ${e.booking} ha pagato${e.fidelity?.applied ? ` · +€ ${e.fidelity.cashback_euros} Fidelity` : ""}`));
        return true;
      }
    } catch { /* cassa is optional */ } finally { setCassaLoading(false); }
    return false;
  }, [day]);

  const [dayPositions, setDayPositions] = useState([]);
  const fetchDay = useCallback(async () => {
    const [s, b, g] = await Promise.all([settings ? Promise.resolve(settings) : fetchSalaSettings(), fetchSalaBookings(day), fetchGroups(day).catch(() => ({ groups: [], positions: [] }))]);
    setSettings(s); setRows(b); setGroups(g.groups || []); setDayPositions(g.positions || []);
    setSelected((cur) => (cur ? b.find((x) => x.id === cur.id) || null : null));
  }, [day, settings]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      await fetchDay();                       // tavoli e prenotazioni subito…
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(PIN_KEY); setAuthed(false); } else toast.error("Errore caricamento");
    } finally { setLoading(false); }
    // …poi TheFork e Cassa in background: se portano novità, ricarico senza bloccare
    const [tf, cassaChanged] = await Promise.all([silent ? Promise.resolve(null) : syncTheFork().catch(() => null), loadCassa()]);
    if (tf?.processed || cassaChanged) fetchDay().catch(() => {});
  }, [fetchDay, loadCassa]);

  useEffect(() => { if (authed) load(); }, [authed, day]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!authed) return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") load(true); }, 45000);
    return () => clearInterval(t);
  }, [authed, load]);

  const services = settings?.services || {};
  const visible = useMemo(() => rows.filter((b) => b.status !== "cancelled" && !b.needs_time && (service === "all" || b.service === service)).sort((a, b) => a.time.localeCompare(b.time)), [rows, service]);
  const pending = useMemo(() => rows.filter((b) => b.needs_time && b.status === "confirmed"), [rows]);
  const covers = (key) => rows.filter((b) => (key === "all" || b.service === key) && ACTIVE.includes(b.status)).reduce((n, b) => n + (b.party_size || 0), 0);
  const seatedNow = visible.filter((b) => b.status === "seated").reduce((n, b) => n + b.party_size, 0);

  const assign = async (b, tableName) => {
    try {
      const body = b._addTable ? { tables: [...bookingTables(b), tableName] } : { table: tableName };
      const r = await patchSalaBooking(b.id, body);
      toast.success(`${b.name} → ${bookingTables(r).join(" + ") || tableName}`); setAssigning(null); load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const pickTable = (t, use, group) => {
    const b = use.find((x) => x.status === "seated") || use.find((x) => x.status === "confirmed") || use[0];
    if (b) setSelected(b);
    else if (group) setWalkIn({ table: group.tables[0], tables: group.tables, groupId: group.id });
    else setWalkIn({ table: t.name });
  };
  const merge = async (names) => {
    try { const g = await mergeTables(day, names); toast.success(`Tavolata: ${g.tables.join(" + ")}`); load(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unione non riuscita"); load(true); }
  };
  // Spostamenti validi solo per il giorno scelto: la mappa di default (Admin) non cambia
  const move = async (positions) => {
    setDayPositions((cur) => { const m = new Map(cur.map((p) => [p.name, p])); positions.forEach((p) => m.set(p.name, p)); return [...m.values()]; });
    try { await moveTables(positions, day); } catch { toast.error("Posizione non salvata"); }
  };
  const resetPositions = async () => {
    try { await resetDayPositions(day); setDayPositions([]); toast.success("Disposizione di default ripristinata"); } catch { toast.error("Errore"); }
  };
  // Modalità modifica mappa: lo spostamento diventa la nuova disposizione di default (per tutti i giorni)
  const moveDefault = async (positions) => {
    setDayPositions((cur) => cur.filter((p) => !positions.some((q) => q.name === p.name)));
    try { const r = await moveTables(positions, day, true); setSettings((s) => ({ ...s, tables: r.tables })); }
    catch { toast.error("Posizione non salvata"); }
  };
  const alignTables = async () => {
    try { const r = await alignLayout(); setSettings((s) => ({ ...s, tables: r.tables })); toast.success("Tavoli allineati"); }
    catch { toast.error("Allineamento non riuscito"); }
  };
  const resetDefault = async () => {
    if (!window.confirm("Riportare tutti i tavoli alla piantina originale? Nomi, posti e aree restano come sono.")) return;
    try { const r = await resetDefaultLayout(); setSettings((s) => ({ ...s, tables: r.tables })); toast.success("Piantina originale ripristinata"); }
    catch { toast.error("Errore"); }
  };
  const dayTables = useMemo(() => (settings?.tables || []).map((t) => { const p = dayPositions.find((x) => x.name === t.name); return p ? { ...t, x: p.x, y: p.y } : t; }), [settings, dayPositions]);
  const persistLayout = async (tables, areas) => {
    try { const r = await saveLayout(tables, areas); setSettings((s) => ({ ...s, tables: r.tables, areas: r.areas })); toast.success("Mappa salvata"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio mappa"); }
  };
  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) { setResults(null); return; }
    try { setResults(await searchSalaBookings(q.trim())); } catch { toast.error("Ricerca fallita"); }
  };

  if (!authed) return <StaffPinGate title="Ristorante · Sala" onUnlock={() => setAuthed(true)} />;
  const today = iso(new Date());

  return (
    <div className="min-h-screen bg-[#f7ebdf] text-[#2b1410]" data-testid="sala-page">
      <header className="sticky top-0 z-30 bg-[#4a1c17] text-[#f7ebdf] shadow-md pt-[env(safe-area-inset-top)]">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3 flex-wrap">
          <img src="/icons/icon-ristorante-192.png" alt="" className="h-10 w-10 rounded-xl object-cover" />
          <div className="flex items-center gap-1 bg-white/10 rounded-full p-1">
            <button onClick={() => setDay(shift(day, -1))} className="h-9 w-9 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid="sala-prev-day"><ChevronLeft className="h-4 w-4" /></button>
            <DayPicker value={day} onChange={setDay} fetchMonth={fetchSalaMonth} onClosure={applyClosure} refreshKey={closureKey}
              trigger={<button className={`h-9 rounded-full px-3 text-sm font-bold capitalize inline-flex items-center gap-1.5 ${day === today ? "bg-white text-[#4a1c17]" : "hover:bg-white/10"}`} data-testid="sala-day-label"><DayPickerIcon /> {day === today ? "Oggi" : longDate(day)}</button>} />
            <button onClick={() => setDay(shift(day, 1))} className="h-9 w-9 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid="sala-next-day"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="flex items-center gap-1 bg-white/10 rounded-full p-1">
            {[...Object.entries(services), ["all", { label: "Tutto" }]].map(([k, v]) => (
              <button key={k} onClick={() => setService(k)} data-testid={`sala-service-${k}`} className={`h-9 rounded-full px-3 text-sm font-bold inline-flex items-center gap-1.5 ${service === k ? "bg-white text-[#4a1c17]" : "hover:bg-white/10"}`}>
                {v.label} <span className={`text-[11px] rounded-full px-1.5 ${service === k ? "bg-[#4a1c17] text-white" : "bg-white/20"}`}>{covers(k)}</span>
              </button>
            ))}
          </div>
          <form onSubmit={search} className="relative flex-1 min-w-[160px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#f7ebdf]/60" />
            <input value={q} onChange={(e) => { setQ(e.target.value); if (!e.target.value) setResults(null); }} placeholder="Cerca nome o telefono…" className="w-full h-10 rounded-full bg-white/10 placeholder:text-[#f7ebdf]/50 pl-9 pr-3 text-sm outline-none focus:bg-white/20" data-testid="sala-search" />
          </form>
          <div className="flex-1" />
          <button onClick={() => load()} className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="sala-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
          <button onClick={() => setWalkIn({})} className="h-10 rounded-full bg-[#C48B31] hover:bg-[#a9741f] px-4 text-sm font-bold inline-flex items-center gap-1.5" data-testid="sala-create-btn"><Plus className="h-4 w-4" /> Crea</button>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 pb-24 lg:pb-4">
        <div className="flex items-center gap-3 mb-2 text-sm">
          <span className="inline-flex items-center gap-1.5 font-semibold whitespace-nowrap"><Users className="h-4 w-4 text-[#C48B31]" /> {visible.length} prenotazioni · {covers(service)} coperti</span>
          {seatedNow > 0 && <span className="rounded-full bg-[#4a1c17] text-white px-2.5 py-0.5 text-xs font-bold whitespace-nowrap" data-testid="sala-seated-now">{seatedNow} al tavolo ora</span>}
          <div className="flex-1" />
          <div className="lg:hidden flex bg-white border border-[#e8dcc8] rounded-full p-0.5">
            <button onClick={() => setMobileView("list")} className={`h-8 rounded-full px-3 text-xs font-bold inline-flex items-center gap-1 ${mobileView === "list" ? "bg-[#4a1c17] text-white" : ""}`} data-testid="sala-view-list"><List className="h-3.5 w-3.5" /> Lista</button>
            <button onClick={() => setMobileView("plan")} className={`h-8 rounded-full px-3 text-xs font-bold inline-flex items-center gap-1 ${mobileView === "plan" ? "bg-[#4a1c17] text-white" : ""}`} data-testid="sala-view-plan"><LayoutGrid className="h-3.5 w-3.5" /> Sala</button>
          </div>
        </div>
        <div className="mb-3 flex flex-col lg:flex-row lg:items-center gap-2" data-testid="sala-top-strip">
          {monthInfo && <div className="shrink-0"><ClosureBanner day={day} monthInfo={monthInfo} onClosure={applyClosure} compact /></div>}
          {cassa && cassa.configured !== false && (
            <div className="min-w-0 flex-1"><CassaSummary data={cassa} services={services} service={service} loading={cassaLoading} compact onSync={async () => { if (await loadCassa(true)) load(true); else toast.message("Cassa aggiornata"); }} /></div>
          )}
        </div>

        {results && (
          <div className="mb-4 rounded-3xl bg-white border border-[#C48B31]/40 p-3" data-testid="sala-search-results">
            <div className="flex items-center justify-between mb-2"><span className="text-sm font-bold">Risultati per “{q}” · {results.length}</span><button onClick={() => { setResults(null); setQ(""); }} className="h-8 w-8 rounded-full hover:bg-[#faf3ee] flex items-center justify-center"><X className="h-4 w-4" /></button></div>
            <ul className="divide-y divide-[#f1e6da] max-h-64 overflow-y-auto">
              {results.map((b) => <li key={b.id}><button onClick={() => { setDay(b.date); setService(b.service || "all"); setSelected(b); setResults(null); }} className="w-full text-left py-2 text-sm flex items-center gap-3 hover:bg-[#faf3ee] rounded-xl px-2" data-testid={`sala-result-${b.id}`}><span className="font-mono text-xs text-[#7c5a4e]">{b.date.split("-").reverse().join("/")} {b.time}</span><span className="font-bold">{b.name}</span><span className="text-[#7c5a4e]">{b.party_size}p</span><span className="ml-auto text-xs">{b.status}</span></button></li>)}
              {!results.length && <li className="py-3 text-sm italic text-[#7c5a4e]">Nessun risultato</li>}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.5fr)] gap-3 lg:gap-4 relative">
          <div className={`space-y-3 lg:max-h-[calc(100vh-210px)] lg:overflow-y-auto no-scrollbar ${mobileView === "list" ? "" : "hidden lg:block"}`} data-testid="sala-list-col">
            <PendingTimeList rows={pending} services={services} onChanged={() => load(true)} />
            <BookingList rows={visible} selectedId={selected?.id} onSelect={(b) => { setSelected(b); setAssigning(null); }} />
          </div>
          <div className={`${mobileView === "plan" ? "" : "hidden lg:block"} relative`} data-testid="sala-plan-col">
            {settings && (
              <FloorPlan tables={dayTables} areas={settings.areas} dayMoved={dayPositions.length > 0} onResetPositions={resetPositions} rows={rows.filter((b) => service === "all" || b.service === service)} groups={groups} mealMin={settings.meal_minutes || 90}
                assigning={assigning} onAssign={assign} onPickTable={pickTable} onWalkIn={() => setWalkIn({})} onMerge={merge} onMove={move} onMoveDefault={moveDefault} onAlign={alignTables} onResetDefault={resetDefault} onSaveLayout={persistLayout} selectedTable={selected?.table} pos={cassa?.tables || {}} service={service} />
            )}
            {selected && (
              <div className="fixed inset-0 z-40 lg:absolute lg:inset-auto lg:top-0 lg:right-0 lg:bottom-0 lg:w-[26rem] lg:max-w-full lg:rounded-3xl lg:shadow-2xl lg:border lg:border-[#e8dcc8] overflow-hidden" data-testid="sala-drawer-wrap">
                <BookingDrawer b={selected} tables={settings?.tables || []} groups={groups} onClose={() => setSelected(null)} onChanged={() => load(true)}
                  onAssignMode={(b, addTable = false) => { setAssigning(addTable ? { ...b, _addTable: true } : b); setSelected(null); setMobileView("plan"); toast.message(addTable ? "Tocca il tavolo da aggiungere alla tavolata" : "Tocca il tavolo sulla piantina"); }} />
              </div>
            )}
          </div>
        </div>
      </main>

      {walkIn && <WalkInDialog date={day} table={walkIn.table} groupTables={walkIn.tables} groupId={walkIn.groupId} tables={settings?.tables || []} settings={settings} rows={rows} onClose={() => setWalkIn(null)} onCreated={(b) => { if (b?.service && service !== "all") setService(b.service); load(true); }} />}
      <Toaster position="top-center" richColors />
    </div>
  );
}
